import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN as string;

interface PublicScene {
  sceneId: string;
  sceneName: string;
  sceneAddress: string;
  sceneOrder: number;
  departureTime: string | null;
  pinLat: number | null;
  pinLng: number | null;
  pinType: string | null;
  pinLocationId: string | null;
}

interface PublicSupport {
  id: string;
  locationId: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
}

interface PublicMapData {
  day: {
    name: string;
    shootDate: string | null;
    basecampName: string | null;
    basecampAddress: string | null;
    basecampLat: number | null;
    basecampLng: number | null;
    projectName: string;
  };
  scenes: PublicScene[];
  supports: PublicSupport[];
}

export function PublicMapView() {
  const { token } = useParams<{ token: string }>();
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [data, setData] = useState<PublicMapData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/public/maps/${token}`)
      .then(r => r.json())
      .then((res) => {
        const r = res as { success: boolean; data: PublicMapData; error?: string };
        if (!r.success) { setError(r.error ?? 'Not found'); return; }
        setData(r.data);
      })
      .catch(() => setError('Failed to load map'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!data || !mapContainer.current || mapRef.current) return;

    mapboxgl.accessToken = TOKEN;
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-118.2437, 34.0522],
      zoom: 10,
    });
    mapRef.current = map;

    map.on('load', () => {
      const bounds = new mapboxgl.LngLatBounds();
      let hasPoint = false;

      const addMarker = (lng: number, lat: number, emoji: string, label: string) => {
        const el = document.createElement('div');
        el.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:default;';
        el.innerHTML = `<div style="font-size:22px;filter:drop-shadow(0 1px 3px rgba(0,0,0,.4))">${emoji}</div><div style="background:white;border-radius:4px;padding:1px 5px;font-size:10px;font-weight:600;color:#1a1a1a;box-shadow:0 1px 4px rgba(0,0,0,.2);white-space:nowrap;max-width:120px;overflow:hidden;text-overflow:ellipsis">${label}</div>`;
        const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([lng, lat])
          .addTo(map);
        markersRef.current.push(marker);
        bounds.extend([lng, lat]);
        hasPoint = true;
      };

      // Scenes
      data.scenes.forEach(scene => {
        if (scene.pinLat != null && scene.pinLng != null) {
          const emoji = scene.pinType === 'basecamp' ? '🚐' : '⭐';
          addMarker(scene.pinLng, scene.pinLat, emoji, scene.sceneName);
        }
      });

      // Basecamp from day
      if (data.day.basecampLat != null && data.day.basecampLng != null) {
        addMarker(data.day.basecampLng, data.day.basecampLat, '🚐', data.day.basecampName ?? 'Basecamp');
      }

      // Support locations
      data.supports.forEach(sup => {
        const nameLower = sup.name.toLowerCase();
        let emoji = '📍';
        if (nameLower.includes('parking')) emoji = 'P';
        else if (nameLower.includes('lunch') || nameLower.includes('dinner') || nameLower.includes('catering')) emoji = '🍽️';
        else if (nameLower.includes('hospital')) emoji = '🏥';

        const el = document.createElement('div');
        el.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:default;';
        if (emoji === 'P') {
          el.innerHTML = `<div style="width:22px;height:22px;background:#4361ee;border-radius:6px;display:flex;align-items:center;justify-content:center;color:white;font-weight:900;font-size:14px;box-shadow:0 1px 3px rgba(0,0,0,.4)">P</div><div style="background:white;border-radius:4px;padding:1px 5px;font-size:10px;font-weight:600;color:#1a1a1a;box-shadow:0 1px 4px rgba(0,0,0,.2);white-space:nowrap;max-width:100px;overflow:hidden;text-overflow:ellipsis">${sup.name}</div>`;
        } else {
          el.innerHTML = `<div style="font-size:18px;filter:drop-shadow(0 1px 3px rgba(0,0,0,.4))">${emoji}</div><div style="background:white;border-radius:4px;padding:1px 5px;font-size:10px;font-weight:600;color:#1a1a1a;box-shadow:0 1px 4px rgba(0,0,0,.2);white-space:nowrap;max-width:100px;overflow:hidden;text-overflow:ellipsis">${sup.name}</div>`;
        }
        const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([sup.lng, sup.lat])
          .addTo(map);
        markersRef.current.push(marker);
        bounds.extend([sup.lng, sup.lat]);
        hasPoint = true;
      });

      if (hasPoint) {
        map.fitBounds(bounds, { padding: 60, maxZoom: 15, duration: 0 });
      }
    });

    return () => {
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, [data]);

  if (loading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-400 text-sm">Loading map…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 font-medium">{error ?? 'Map not found'}</p>
          <p className="text-gray-400 text-sm mt-1">This link may have expired or be invalid.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen flex overflow-hidden">
      {/* Left panel */}
      <div className="w-72 shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">{data.day.projectName}</p>
          <h1 className="text-lg font-bold text-gray-900 mt-0.5">{data.day.name}</h1>
          {data.day.shootDate && (
            <p className="text-sm text-gray-500 mt-0.5">
              {new Date(data.day.shootDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {data.scenes.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No scenes scheduled</p>
          ) : (
            data.scenes.map((scene, idx) => (
              <div key={scene.sceneId} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-400 w-5 shrink-0">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{scene.sceneName}</p>
                    {scene.sceneAddress && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">{scene.sceneAddress}</p>
                    )}
                    {scene.departureTime && (
                      <p className="text-xs text-brand-600 font-medium mt-0.5">🕐 {scene.departureTime}</p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 text-center">
          <p className="text-[10px] text-gray-300 font-medium">Powered by Location Breaker</p>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        <div ref={mapContainer} className="w-full h-full" />
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-sm border border-gray-200">
          <p className="text-[10px] text-gray-400 font-medium">Powered by Location Breaker</p>
        </div>
      </div>
    </div>
  );
}
